import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectMapPreference, setMapPreference } from 'slices/route/persistentMapPreferencesSlice'

// Drop-in for useState that persists to the per-map preferences slice, so a map
// remembers its own control value (basemap, a layer toggle, an offset) across
// reopen + session. Supports functional updates like useState.
//
//   const [isSatellite, setIsSatellite] = useMapPreference('changeContainer', 'isSatellite', false)
//
// `mapKey` namespaces the value per surface; pass a distinct key per map.
export const useMapPreference = (mapKey, field, defaultValue) => {
  const dispatch = useDispatch()
  const stored = useSelector(selectMapPreference(mapKey, field))
  const value = stored === undefined ? defaultValue : stored

  // Keep the latest value in a ref so setValue stays referentially stable (like
  // a useState setter) — handlers can keep `[]` deps without stale closures.
  const valueRef = useRef(value)
  valueRef.current = value

  const setValue = useCallback(
    next => {
      const resolved = typeof next === 'function' ? next(valueRef.current) : next
      dispatch(setMapPreference({ mapKey, field, value: resolved }))
    },
    [dispatch, mapKey, field]
  )

  return [value, setValue]
}

export default useMapPreference
